import React, { useState, useCallback } from "react";
import { Steps } from "primereact/steps";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import { GitLabProject } from "../types/gitlab";
import { LinearIssue } from "../services/linearService";
import { gitlabService } from "../services/gitlabService";
import Header from "./Header";
import ProjectSelector from "./ProjectSelector";
import LinearSelectionStep from "./LinearSelectionStep";
import ReleaseProcessStep from "./ReleaseProcessStep";

type WizardStep = "linear" | "projects" | "process";

const Dashboard: React.FC = () => {
  const [selectedIssues, setSelectedIssues] = useState<LinearIssue[]>([]);
  const [releaseName, setReleaseName] = useState("");
  const [gitTagName, setGitTagName] = useState("");
  const [releaseDescription, setReleaseDescription] = useState("");

  const [selectedProjects, setSelectedProjects] = useState<GitLabProject[]>([]);
  const [suggestedProjectIds, setSuggestedProjectIds] = useState<number[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const [currentStep, setCurrentStep] = useState<WizardStep>("linear");

  const steps = [
    { label: "Linear Issues" },
    { label: "Select Projects" },
    { label: "Release Process" },
  ];

  const getActiveIndex = () => {
    switch (currentStep) {
      case "linear":
        return 0;
      case "projects":
        return 1;
      case "process":
        return 2;
      default:
        return 0;
    }
  };

  const handleNextToProjects = useCallback(async () => {
    setCurrentStep("projects");

    if (selectedIssues.length > 0 && gitlabService.isConfigured()) {
      setIsLoadingSuggestions(true);
      try {
        const identifiers = selectedIssues.map((issue) => issue.identifier);
        const projectIds = await gitlabService.findProjectsForIssueKeys(identifiers);
        setSuggestedProjectIds(projectIds);
      } catch (error) {
        console.error("Error searching for affected projects:", error);
      } finally {
        setIsLoadingSuggestions(false);
      }
    }
  }, [selectedIssues]);

  const handleNextToProcess = () => {
    if (selectedProjects.length > 0) {
      setCurrentStep("process");
    }
  };

  const handleBackToLinear = () => {
    setCurrentStep("linear");
  };

  const handleBackToProjects = () => {
    setCurrentStep("projects");
  };

  const handleStartOver = () => {
    setSelectedIssues([]);
    setSelectedProjects([]);
    setSuggestedProjectIds([]);
    setReleaseName("");
    setGitTagName("");
    setReleaseDescription("");
    setCurrentStep("linear");
  };

  return (
    <div className="dashboard">
      <Header />

      <main className="dashboard-content p-4">
        <Card className="mb-4">
          <Steps
            model={steps}
            activeIndex={getActiveIndex()}
            readOnly
            className="mb-0"
          />
        </Card>

        {currentStep === "linear" && (
          <LinearSelectionStep
            selectedIssues={selectedIssues}
            releaseName={releaseName}
            gitTagName={gitTagName}
            releaseDescription={releaseDescription}
            onIssuesChange={setSelectedIssues}
            onReleaseNameChange={setReleaseName}
            onGitTagNameChange={setGitTagName}
            onReleaseDescriptionChange={setReleaseDescription}
            onNext={handleNextToProjects}
          />
        )}

        {currentStep === "projects" && (
          <>
            <div className="flex justify-content-between align-items-center mb-4">
              <Button
                label="Back to Linear Issues"
                icon="pi pi-arrow-left"
                className="p-button-outlined"
                onClick={handleBackToLinear}
              />
              <div className="flex align-items-center gap-2">
                <Chip
                  label={`${selectedIssues.length} issues`}
                  icon="pi pi-ticket"
                />
                <Chip label={releaseName} icon="pi pi-bookmark" />
                <Chip label={gitTagName} icon="pi pi-tag" />
              </div>
            </div>

            <ProjectSelector
              selectedProjects={selectedProjects}
              onSelectionChange={setSelectedProjects}
              suggestedProjectIds={suggestedProjectIds}
              isLoadingSuggestions={isLoadingSuggestions}
            />

            <div className="flex justify-content-end mt-4">
              <Button
                label={`Next: Start Release (${selectedProjects.length} projects)`}
                icon="pi pi-arrow-right"
                iconPos="right"
                disabled={selectedProjects.length === 0}
                onClick={handleNextToProcess}
                className="p-button-lg"
              />
            </div>
          </>
        )}

        {currentStep === "process" && (
          <ReleaseProcessStep
            selectedProjects={selectedProjects}
            selectedIssues={selectedIssues}
            releaseName={releaseName}
            gitTagName={gitTagName}
            releaseDescription={releaseDescription}
            onBack={handleBackToProjects}
            onStartOver={handleStartOver}
          />
        )}
      </main>
    </div>
  );
};

export default Dashboard;
